import { useMutation } from "@tanstack/react-query";
import { AddressValidationService } from "@/services/address-validation/address-validation-service";
import type {
  AddressSuggestion,
  AddressValidationRequest,
  AddressValidationResponse,
  ValidationError,
} from "@/services/address-validation/address-validation.types";

const addressValidationService = new AddressValidationService();

export type AddressValidationOutcome =
  | { status: "proceed" }
  | { status: "suggestionAvailable"; mode: "validWithChanges"; suggestion: AddressSuggestion }
  | { status: "suggestionAvailable"; mode: "invalidWithSuggestions"; suggestion: AddressSuggestion }
  | { status: "invalid"; validationErrors: ValidationError[] }
  | { status: "crossCountryNotAllowed" };

const classifyOutcome = (response: AddressValidationResponse): AddressValidationOutcome => {
  // HOP's contract is a list, but HOP returns at most one suggestion.
  const suggestion = response.addressSuggestions?.[0];
  switch (response.resultCode) {
    case "VALID_WITH_CHANGES":
      return suggestion
        ? { status: "suggestionAvailable", mode: "validWithChanges", suggestion }
        : { status: "proceed" };
    case "INVALID":
      return suggestion
        ? { status: "suggestionAvailable", mode: "invalidWithSuggestions", suggestion }
        : { status: "invalid", validationErrors: response.validationErrors ?? [] };
    case "CROSS_COUNTRY_NOT_ALLOWED":
      return { status: "crossCountryNotAllowed" };
    default:
      return { status: "proceed" };
  }
};

/**
 * Reusable address-validation mutation. Owns the HOP call and classifies the
 * result into the outcomes a caller needs to act on. Does not fail open on
 * request failure -- callers proceeding on error is a checkout-specific
 * decision, not a property of validation itself.
 */
export const useAddressValidation = () => {
  const mutation = useMutation<AddressValidationOutcome, Error, AddressValidationRequest>({
    mutationFn: (request) => addressValidationService.validateAddress(request).then(classifyOutcome),
  });

  return {
    validateAddress: mutation.mutateAsync,
    isValidating: mutation.isPending,
  };
};
